import {
  LanguageType,
  ProductPrices,
  ProductWeightsAndDimensions,
  ProductVariant,
} from '@tiendanube/common';
import type { ActionHandlerResult } from 'commons/hooks/useAiField/types';

export interface ComponentController<T = unknown, V = unknown> {
  getValue?: () => T | undefined;
  onReplace?: (content: T | V) => ActionHandlerResult<V>;
  onAdd?: (content: T | V) => ActionHandlerResult<V>;
  onRemove?: (content: T | V) => ActionHandlerResult<V>;
  onClear?: () => void;
}

type StateEntry<K extends string, T> = {
  [key in K]?: {
    value?: T;
  };
};

/**
 * Defines the structured keys for the AgentChatActionBusState. When backend sends a new state action target, it should be added here.
 */
export const AI_FIELDS = {
  PRODUCT_NAME: 'product.name',
  PRODUCT_DESCRIPTION: 'product.description',
  PRODUCT_SEO_TITLE: 'product.seo.title',
  PRODUCT_SEO_DESCRIPTION: 'product.seo.description',
  PRODUCT_SEO_URL: 'product.seo.url',
  PRODUCT_TAGS: 'product.tags',
  PRODUCT_PRICES: 'product.prices',
  PRODUCT_WEIGHTS_AND_DIMENSIONS: 'product.weights_and_dimensions',
  PRODUCT_BRAND: 'product.brand',
  PRODUCT_CATEGORIES: 'product.categories',
  PRODUCT_HIGHLIGHTS: 'product.highlights',
  PRODUCT_PUBLISHED: 'product.published',
  PRODUCT_FREE_SHIPPING: 'product.free_shipping',
  PRODUCT_IS_DIGITAL: 'product.is_digital',
  PRODUCT_VARIANTS: 'product.variants',
  PRODUCT_IMAGES: 'product.images',
} as const;

/**
 * Mapping between the state keys and the state values. For each action target, there's a specific expected type.
 */
export type AgentChatActionBusState = StateEntry<
  typeof AI_FIELDS.PRODUCT_NAME,
  Partial<Record<LanguageType, string>>
> &
  StateEntry<
    typeof AI_FIELDS.PRODUCT_DESCRIPTION,
    Partial<Record<LanguageType, string>>
  > &
  StateEntry<
    typeof AI_FIELDS.PRODUCT_SEO_TITLE,
    Partial<Record<LanguageType, string>>
  > &
  StateEntry<
    typeof AI_FIELDS.PRODUCT_SEO_DESCRIPTION,
    Partial<Record<LanguageType, string>>
  > &
  StateEntry<typeof AI_FIELDS.PRODUCT_SEO_URL, string> &
  StateEntry<typeof AI_FIELDS.PRODUCT_TAGS, string[]> &
  StateEntry<typeof AI_FIELDS.PRODUCT_PRICES, ProductPrices> &
  StateEntry<
    typeof AI_FIELDS.PRODUCT_WEIGHTS_AND_DIMENSIONS,
    ProductWeightsAndDimensions
  > &
  StateEntry<typeof AI_FIELDS.PRODUCT_BRAND, string> &
  StateEntry<typeof AI_FIELDS.PRODUCT_CATEGORIES, string[]> &
  StateEntry<typeof AI_FIELDS.PRODUCT_HIGHLIGHTS, string[]> &
  StateEntry<typeof AI_FIELDS.PRODUCT_PUBLISHED, boolean> &
  StateEntry<typeof AI_FIELDS.PRODUCT_FREE_SHIPPING, boolean> &
  StateEntry<typeof AI_FIELDS.PRODUCT_IS_DIGITAL, boolean> &
  StateEntry<typeof AI_FIELDS.PRODUCT_VARIANTS, ProductVariant[]> &
  StateEntry<typeof AI_FIELDS.PRODUCT_IMAGES, string[]>;
